import React, { useEffect, useMemo, useState } from 'react'
import queryString from 'query-string'
import {
  EvidenceGroup,
  EvidenceGroupId,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { useInAudit } from 'src/context/AuditContext'
import { getIsMultiPartMixedMime } from 'src/Utils/globalHelpers'
import { ListDocumentRequestsRequest } from '@trustero/trustero-api-web/lib/request/request_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { useDocumentRequests } from 'src/components/async/DocumentRequest'
import { useLocation } from 'react-router-dom'
import isFunction from 'lodash/isFunction'
import log from 'loglevel'
import { showInfoToast, ToastPrompts } from 'src/Utils/helpers/toast'
import { isViewable } from 'src/components/ModalForms/Evidence/ViewEvidenceForm/ViewEvidenceForm.utils'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { Tabs } from 'src/components/Reusable/Tabs'
import { TabPanelButtonGroup } from 'src/components/Reusable/Tabs/TabNotes/styles'
import {
  PrimaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import { PERMISSIONS } from 'src/config/roleConfig'
import { ModalFormId } from 'src/components/ModalForms'
import { useModalState } from 'src/Modal/ModalStateContext'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { isTrArtifactMime } from 'src/components/ModalForms/Evidence/evidenceUtils'
import { useContent } from 'src/context/hooks'
import { ContentStaticHelper } from 'src/context/Content/statichelpers'
import {
  useEvidenceConfigs,
  useEvidenceSources,
  useGetEvidenceControls,
} from '../../evidence.hooks'
import { CurrentEvidenceGroupProvider } from '../../context/CurrentEvidenceGroupContext'
import { getIsLongText } from '../../evidence.helpers'
import { EvidenceConfigType } from '../../evidence.constants'
import { EvidencePropertiesBody } from './properties/Properties'
import { EVIDENCE_TABS } from './EvidenceTabs.constants'
import { EvidenceTabContainer } from './EvidenceTabs.styles'
import { EvidenceBodies } from './evidence/EvidenceBody'
import { EvidenceExcludedBody } from './excluded/EvidenceExcludedBody'
import { EvidenceControlsBody } from './controls/EvidenceControlsBody'
import { EvidenceDocumentRequestsBody } from './requests/EvidenceDocumentRequestsBody'
import { EvidenceSourceBody } from './source/EvidenceSourceBody'
import { ArtifactSourceBody } from './source/ArtifactSourceBody'
import { useEvidenceControlMappingsTabs } from './EvidenceTabs.hooks'

const ControlsTabNote = (): JSX.Element => {
  const { openModal } = useModalState()
  const canLinkEvidenceControls = useCanPerformAction(
    CAPABILITY.CAN_LINK_EVIDENCE_CONTROLS,
  )
  return (
    <TabPanelButtonGroup>
      <PrimaryButton
        size={StandardButtonSize.SMALL}
        requiredPermissions={[PERMISSIONS.EDIT]}
        text="Link to Controls"
        onClick={() => openModal(ModalFormId.LINK_EVIDENCE_TO_CONTROLS)}
        isDisabled={!canLinkEvidenceControls}
        disabledReason="You do not have permission to link evidence to controls."
      />
    </TabPanelButtonGroup>
  )
}

const DocumentRequestsTabNote = (): JSX.Element => {
  const { openModal } = useModalState()
  const canLinkEvidenceRequests = useCanPerformAction(
    CAPABILITY.CAN_LINK_EVIDENCE_REQUESTS,
  )
  return (
    <TabPanelButtonGroup>
      <PrimaryButton
        size={StandardButtonSize.SMALL}
        requiredPermissions={[PERMISSIONS.EDIT]}
        text="Link to Requests"
        onClick={() => openModal(ModalFormId.LINK_EVIDENCE_TO_REQUESTS)}
        isDisabled={!canLinkEvidenceRequests}
        disabledReason="You do not have permission to link evidence to requests."
      />
    </TabPanelButtonGroup>
  )
}

export const EvidenceTabs = ({
  evidence,
}: {
  evidence: EvidenceGroup
}): JSX.Element => {
  const hasEvidenceControlMappings = IsGatekeeperEnabled(
    GatekeeperFlag.CONTROL_EVIDENCE_MAPPINGS,
  )
  const isNewRequests = IsGatekeeperEnabled(GatekeeperFlag.NEW_REQUESTS)
  const mime = evidence.getMime()
  const content = useContent()
  const actorEmail = evidence.getActorEmail()
  const hasValidReceptor =
    !actorEmail.includes('@') &&
    !!ContentStaticHelper.getObjById(content, actorEmail)?.logo
  const isAutomated = evidence.getIsAutomated() || hasValidReceptor
  const { auditId } = useInAudit()
  const [configs, setConfigs] = useState<EvidenceConfigType[] | null>(null)
  const [sourcesMarkdown, setSourcesMarkdown] = useState<string>('')
  const [activeTabTitle, setActiveTabTitle] = useState<string>(
    () =>
      (queryString.parse(window.location.search, { arrayFormat: 'bracket' })
        ?.tab as string) ?? '',
  )
  const [evidenceId, setEvidenceId] = useState<string>(evidence.getId())
  const contentId = evidence.getContentId()
  const caption = evidence.getCaption()
  const discoveryId = evidence.getDiscoveryId()
  const isMultipart = getIsMultiPartMixedMime(mime)
  const isTrArtifact = isTrArtifactMime(mime)
  const isEvidenceAutomated = isAutomated
  const getEvidenceConfigs = useEvidenceConfigs(
    contentId,
    mime,
    isEvidenceAutomated,
    caption,
  )
  const getEvidenceSources = useEvidenceSources(
    contentId,
    mime,
    isEvidenceAutomated,
  )
  const { data: evidenceControlsData, isLoading: evidenceControlsLoading } =
    useGetEvidenceControls(evidence.getEvidenceId())
  const request = new ListDocumentRequestsRequest()
  const evidenceGroupId = new EvidenceGroupId()
    .setCaption(new StringValue().setValue(caption))
    .setContentId(new StringValue().setValue(contentId))
  discoveryId &&
    evidenceGroupId.setDiscoveryId(new StringValue().setValue(discoveryId))
  const { data: requestsData, isLoading: requestsLoading } =
    useDocumentRequests({
      request,
      evidenceGroupId,
      evidenceInstanceId: evidence.getEvidenceInstanceId(),
    })
  const location = useLocation()
  const queryParams = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const queryParamTab = queryParams?.tab
  const queryParamPage = queryParams?.citation_page

  const evidenceControls = useMemo(() => {
    if (evidenceControlsData) {
      return evidenceControlsData.getEvidenceControlsList() || []
    }
    return []
  }, [evidenceControlsData])

  const requests = useMemo(
    () => requestsData?.getItemsList() || [],
    [requestsData],
  )

  const isLinkedDocument = useMemo(() => {
    return evidence.getIsLinkedDocument()
  }, [evidence])

  const pageNumber = useMemo(() => {
    const number = queryParamPage
      ? parseInt(queryParamPage as string)
      : undefined
    if (number === undefined || isNaN(number)) {
      return undefined
    }
    return number
  }, [queryParamPage])

  useEffect(() => {
    if (!isFunction(getEvidenceConfigs)) return
    const fetchConfigs = async () => {
      try {
        const configs = await getEvidenceConfigs()
        if (!configs || !configs.length) return
        setConfigs(configs)
      } catch (err) {
        log.error(
          `Error downloading document in EvidenceTabs - id: ${evidence.getId()}`,
          err,
        )
        showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
      } finally {
        setEvidenceId(evidence.getId())
      }
    }
    fetchConfigs()
  }, [getEvidenceConfigs, evidence, isLinkedDocument])

  useEffect(() => {
    // Reset sources markdown when evidence changes so that sources
    // are refetched for the new evidence item instead of reusing
    // stale cached sources from a previous item.
    setSourcesMarkdown('')
  }, [evidence])

  useEffect(() => {
    if (
      activeTabTitle !== EVIDENCE_TABS.SOURCE.toLowerCase() ||
      !isFunction(getEvidenceSources) ||
      sourcesMarkdown ||
      isTrArtifact
    )
      return
    getEvidenceSources()
      .then((sources) => setSourcesMarkdown(sources || ''))
      .catch((err) => {
        log.error(
          `Error downloading sources in EvidenceTabs - id: ${evidence.getId()}`,
          err,
        )
        showInfoToast(ToastPrompts.DOC_DOWNLOAD_ERROR)
      })
  }, [
    activeTabTitle,
    getEvidenceSources,
    sourcesMarkdown,
    evidence,
    isTrArtifact,
  ])

  const controlMappingTab = useEvidenceControlMappingsTabs({ evidence })
  const includeEvidenceBody = useMemo(() => {
    const isLongText = configs?.some((config) =>
      getIsLongText(config.body, config.mimeType),
    )
    const viewable = configs?.some((config) => isViewable(config.mimeType))
    return isAutomated || viewable || isLongText || isTrArtifact
  }, [isAutomated, configs, isTrArtifact])

  const evidenceTabs = useMemo(() => {
    const tabs = []
    const evidenceTab = {
      title: EVIDENCE_TABS.EVIDENCE,
      body: (
        <EvidenceBodies
          configs={configs}
          caption={caption}
          pageNumber={pageNumber}
        />
      ),
    }

    const excludedTab = {
      title: EVIDENCE_TABS.EXCLUDED,
      body: (
        <EvidenceExcludedBody
          evidenceId={evidence.getId()}
          exclusions={evidence.getAllExclusions()?.getExclusionsList()}
        />
      ),
    }

    const sourceTab = {
      title: EVIDENCE_TABS.SOURCE,
      body: isTrArtifact ? (
        <ArtifactSourceBody configs={configs} />
      ) : (
        <EvidenceSourceBody sourcesMarkdown={sourcesMarkdown} />
      ),
    }

    const controlsCount = evidenceControls.length

    const controlsTab = {
      title: EVIDENCE_TABS.CONTROLS,
      note: controlsCount > 0 ? <ControlsTabNote /> : <></>,
      body: (
        <CurrentEvidenceGroupProvider>
          <EvidenceControlsBody
            evidence={evidence as EvidenceGroup}
            controls={evidenceControls}
            isLoading={evidenceControlsLoading}
          />
        </CurrentEvidenceGroupProvider>
      ),
      count: controlsCount,
    }

    const requestsTab = {
      title: EVIDENCE_TABS.REQUESTS,
      note: requests.length ? <DocumentRequestsTabNote /> : <></>,
      body: (
        <EvidenceDocumentRequestsBody
          evidence={evidence as EvidenceGroup}
          requests={requests}
          isLoading={requestsLoading}
        />
      ),
      count: requests.length,
    }

    const propertiesTab = {
      title: EVIDENCE_TABS.PROPERTIES,
      body: <EvidencePropertiesBody evidence={evidence} />,
    }

    tabs.unshift(propertiesTab)

    if (auditId || isNewRequests) {
      tabs.unshift(requestsTab)
    }

    if (hasEvidenceControlMappings && controlMappingTab) {
      tabs.unshift(controlMappingTab)
    }

    tabs.unshift(controlsTab)

    if (isAutomated || (!isLinkedDocument && isMultipart) || isTrArtifact) {
      tabs.unshift(sourceTab)
    }
    if (isAutomated && !isLinkedDocument && !isTrArtifact) {
      tabs.unshift(excludedTab)
    }

    includeEvidenceBody && tabs.unshift(evidenceTab)
    return tabs
  }, [
    pageNumber,
    evidence,
    isAutomated,
    isMultipart,
    isLinkedDocument,
    includeEvidenceBody,
    auditId,
    caption,
    requestsLoading,
    sourcesMarkdown,
    requests,
    evidenceControls,
    evidenceControlsLoading,
    configs,
    hasEvidenceControlMappings,
    controlMappingTab,
    isTrArtifact,
    isNewRequests,
  ])

  const defaultTab = useMemo(() => {
    const tabIndex = evidenceTabs.findIndex(
      (tab) => tab.title.toLowerCase().split(' ').join('-') === queryParamTab,
    )
    if (tabIndex === -1) {
      return '0'
    }
    return tabIndex.toString()
  }, [queryParamTab, evidenceTabs])

  const onSelect = (key: string | null) => {
    if (!key) return
    const tabName = evidenceTabs[parseInt(key, 10)]
    if (tabName) {
      const newTabValue = tabName.title.toLowerCase().split(' ').join('-')
      setActiveTabTitle(newTabValue)
      const newQuery = {
        ...queryString.parse(location.search, { arrayFormat: 'bracket' }),
        tab: newTabValue,
      }
      const newUrl = `${location.pathname}?${queryString.stringify(newQuery, {
        arrayFormat: 'bracket',
      })}`
      window.history.pushState({}, '', newUrl)
    }
  }

  if (evidenceId !== evidence.getId()) {
    return <TableListLoader />
  }

  return (
    <EvidenceTabContainer>
      <Tabs
        tabs={evidenceTabs}
        defaultActiveKey={defaultTab}
        onSelect={onSelect}
      />
    </EvidenceTabContainer>
  )
}
